/* eslint-disable no-unused-expressions */
/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2022
 * @format
 * @flow
 */

import CApiWebSocketClient from './CApiWebSocketClient';
import EventEmitter from '@utility/eventSubscribers';
import store from '../store';
import socketActionTypes from '../sockets/socketActionTypes';
import {
  SET_SOCKET_CONNECTION
} from '@reducers/oauth';

class CApiWebSocketService {
  cApiWebSocketClient = null;
  isConnected = false;
  logCapiClientHealthInfo = null;
  healthInfo = {
    storeId: 0,
    deviceTypeId: 0,
    deviceExternalId: null,
    connected: false,
    connectCount: 0,
    currentConnectionDurationInMilliseconds: 0,
    averageConnectionDurationInMilliseconds: 0,
    lastConnectDate: null,
    lastDisconnectDate: null,
    currentDate: null,
    lastError: null
  };

  constructor() {
    this.cApiWebSocketClient = new CApiWebSocketClient();
    this.cApiWebSocketClient.onConnected = this.onConnected;
    this.cApiWebSocketClient.onReconnecting = this.onReconnecting;
    this.cApiWebSocketClient.onReconnected = this.onReconnected;
    this.cApiWebSocketClient.onClose = this.onClose;
  }

  onConnected = () => {
    try {
      this.isConnected = true;
      this.subscribeToStoreChecks();
      this.subscribeToStoreCheckPreps()
      this.subscribeToStoreNotifications();
      this.setSyncStatusToConnected();
    
      const previousConnectCount = this.healthInfo.connectCount;
      this.healthInfo.connected = true;
      this.healthInfo.connectCount++;
      this.healthInfo.lastConnectDate = new Date();
      if (previousConnectCount === 0) {
        this.sendHealthInfo(); // Send health info on first connect
      }
    } catch(e) {
      console.error('onConnected error', this.healthInfo, e);
    }
  };

  onReconnecting = (error) => {
    try {
      const errMessage = `Connection lost. Attempting to reconnect. Error:- ${error}`;
      console.warn(errMessage, 'Reconnecting...');
      this.setSyncStatusToDisconnected();

      const previousLastDisconnectDate = this.healthInfo.lastDisconnectDate;
      this.healthInfo.connected = false;
      this.healthInfo.lastDisconnectDate = new Date();
      this.healthInfo.lastError = `${error}`;

      if (this.healthInfo.lastConnectDate && this.healthInfo.lastDisconnectDate) {
        const lastConnectionDurationInMilliseconds = this.healthInfo.lastDisconnectDate - this.healthInfo.lastConnectDate;
        if (this.healthInfo.averageConnectionDurationInMilliseconds === 0) {
          this.healthInfo.averageConnectionDurationInMilliseconds = lastConnectionDurationInMilliseconds;
        } else {
          const newAverage = (this.healthInfo.averageConnectionDurationInMilliseconds + lastConnectionDurationInMilliseconds) / 2;
          this.healthInfo.averageConnectionDurationInMilliseconds = Math.round(newAverage);
        }
      }

      if (previousLastDisconnectDate === null) {
        this.sendHealthInfo(); // Send health info on first disconnect
      }
    } catch(e) {
      console.error('onReconnecting error', this.healthInfo, e);
    }
  };

  onReconnected = () => {
    try {
      this.setSyncStatusToConnected();

      this.healthInfo.connected = true;
      this.healthInfo.connectCount++;
      this.healthInfo.lastConnectDate = new Date();
    } catch(e) {
      console.error('onReconnected error', this.healthInfo, e);
    }
  };

  onClose = (error) => {
    console.error('Client connection has been closed.', error);
  };
  
  connect = (baseUrl, apiKey, deviceExternalId, storeId, deviceTypeId, logCapiClientHealthInfo, subscriber) => {

    if (!baseUrl) {
      console.error('baseUrl is required');
      return;
    }

    if (!apiKey) {
      console.error('apiKey is required');
      return;
    }

    if (!deviceExternalId) {
      console.error('deviceExternalId is required');
      return;
    }

    this.cApiWebSocketClient.onMessageReceived = (message) => {
      subscriber && subscriber(message);
    };

    this.isConnected = false;
    this.healthInfo.storeId = storeId;
    this.healthInfo.deviceTypeId = deviceTypeId;
    this.healthInfo.deviceExternalId = deviceExternalId;
    this.logCapiClientHealthInfo = logCapiClientHealthInfo;
    setInterval(this.sendHealthInfo, 300000); // Every 5 minutes send health info

    this.cApiWebSocketClient.connect(baseUrl, apiKey, deviceExternalId);
  };

  subscribeToStoreChecks = () => {
    if (!this.isConnected) {
      console.error('WebSocket is not connected. Please connect first before subscribing');
      return;
    }

    this.cApiWebSocketClient.subscribeToStoreChecks()
      .catch((err) => {
        console.error(`Error subscribing to store checks. Error:- ${err}`);
      });
  };

  subscribeToStoreCheckPreps = () => {
    if (!this.isConnected) {
      console.error('WebSocket is not connected. Please connect first before subscribing');
      return;
    }

    this.cApiWebSocketClient.subscribeToStoreCheckPreps()
      .catch((err) => {
        console.error(`Error subscribing to store checks. Error:- ${err}`);
      });
  };


  

  subscribeToStoreNotifications = () => {
    if (!this.isConnected) {
      console.error('WebSocket is not connected. Please connect first before subscribing');
      return;
    }

    this.cApiWebSocketClient.subscribeToStoreNotifications()
      .catch((err) => {
        console.error(`Error subscribing to store checks. Error:- ${err}`);
      });
  };

  setSyncStatusToConnected = () => {
    try {
      if (window.ReactNativeWebView) {
        EventEmitter.syncStatus(socketActionTypes.SYNC_STATUS_CONNECTED);
      }
      store.dispatch({
        type: SET_SOCKET_CONNECTION,
        payload: true
      });
    } catch(e) {
      console.error('setSyncStatusToConnected error', e);
    }
  };

  setSyncStatusToDisconnected = () => {
    try {
      if (window.ReactNativeWebView) {
        EventEmitter.syncStatus(socketActionTypes.SYNC_STATUS_DISCONNECTED);
      }
      store.dispatch({
        type: SET_SOCKET_CONNECTION,
        payload: false
      });
    } catch(e) {
      console.error('setSyncStatusToDisconnected error', e);
    }
  };

  sendHealthInfo = () => {
    try {
      this.healthInfo.currentDate = new Date();

      if (this.healthInfo.lastConnectDate && this.healthInfo.connected) {
        this.healthInfo.currentConnectionDurationInMilliseconds = this.healthInfo.currentDate - this.healthInfo.lastConnectDate;
      }

      if (typeof this.logCapiClientHealthInfo === 'function') {
      this.logCapiClientHealthInfo(this.healthInfo)
        .catch((error) => {
          console.error('error logging capi client health info', this.healthInfo, error);
        });
      }
    } catch (e) {
      console.error('error sending capi client health info', this.healthInfo, e);
    }
  };

  // This method is useless and should be refactored away because there is no this.socket property
  sendMessageUncompressed = (event, ...args) => {
    try {
      if (this.socket) {
        this.socket.emit(event, ...args);
      }
    } catch(e) {
      console.error('sendMessageUncompressed error', error);
    }
  };

  // This method is useless and should be refactored away because there is no this.socket property
  sendMessage = (event, ...args) => {
    try {
      if (this.socket && this.connected) {
        this.socket.emit(event, ...args.slice(0, args.length - 1), (...response) => this.customCallback(...response, args[args.length - 1]),
        );
      } else {
        args[args.length - 1] &&
          args[args.length - 1]({
            errorCode: 'socket_disconnected',
          });
      }
    } catch(e) {
      console.error('sendMessage error', error);
    }
  };

  // This method is useless and should be refactored away because there is no this.socket property
  redisSubscribe = (event, storeId, callback, subscriber) => {
    try {
      this.sendMessage(event, storeId, (error, response) => {
        callback(error, response);
        if (!error) {
          const { id } = response;
          this.socket.on(id, (...args) => {
            this.customSubscriber(...args, subscriber);
          });
        }
      });
    } catch(e) {
      console.error('redisSubscribe error', error);
    }
  };
}

export default CApiWebSocketService;